import * as THREE from 'three'
import { Background } from '@/scene/Background'
import { Sun } from '@/scene/Sun'
import { Planet } from '@/scene/Planet'
import { ParticleTrails } from '@/scene/ParticleTrails'
import { celestialBodies } from '@/_constants/bodies'
import type { CelestialBody } from '@/_types/celestial-body'
import type { QualityProfile } from '@/_types/scene'

/**
 * The full QuickCasa Universe scene graph. Builds the backdrop, the sun, every
 * planet, and the connecting trails from the constants, then advances them each
 * frame and exposes the hooks the interaction and camera layers need.
 */
export class SolarSystem {
  public readonly scene: THREE.Scene
  public readonly sun: Sun

  private readonly background: Background
  private readonly planets: Planet[]
  private readonly trails: ParticleTrails
  private readonly bodyById: Map<string, Sun | Planet>
  private readonly bodyPositions: Map<string, THREE.Vector3>

  /**
   * @param {QualityProfile} quality - The resolved device quality profile.
   */
  public constructor(quality: QualityProfile) {
    this.scene = new THREE.Scene()
    this.planets = []
    this.bodyById = new Map<string, Sun | Planet>()
    this.bodyPositions = new Map<string, THREE.Vector3>()

    this.background = new Background(quality)
    this.scene.add(this.background.object)

    const sunBody = this.resolveSunBody()
    this.sun = new Sun(sunBody)
    this.scene.add(this.sun.object)
    this.bodyById.set(sunBody.id, this.sun)
    this.bodyPositions.set(sunBody.id, new THREE.Vector3(0, 0, 0))

    let styleSeed = 0
    for (const body of celestialBodies) {
      if (body.tier === 'sun') {
        continue
      }
      styleSeed += 17.31
      const planet = new Planet(body, styleSeed)
      this.planets.push(planet)
      this.scene.add(planet.object)
      this.bodyById.set(body.id, planet)
      this.bodyPositions.set(body.id, new THREE.Vector3())
    }

    this.trails = new ParticleTrails(quality.pixelRatio)
    this.scene.add(this.trails.object)
  }

  /**
   * Advance the entire scene one frame.
   *
   * @param {number} elapsedSeconds - Total elapsed time since start.
   * @param {number} deltaSeconds - Time since the previous frame.
   * @returns {void}
   */
  public update(elapsedSeconds: number, deltaSeconds: number): void {
    this.background.update(elapsedSeconds, deltaSeconds)
    this.sun.update(elapsedSeconds, deltaSeconds)

    for (const planet of this.planets) {
      planet.update(elapsedSeconds, deltaSeconds)
      const target = this.bodyPositions.get(planet.body.id)
      if (target !== undefined) {
        planet.getWorldPosition(target)
      }
    }

    this.trails.update(elapsedSeconds, this.bodyPositions)
  }

  /**
   * The pick meshes for every currently visible body, for raycasting.
   *
   * @returns {THREE.Object3D[]} The visible pick meshes.
   */
  public get pickMeshes(): THREE.Object3D[] {
    const meshes: THREE.Object3D[] = [this.sun.pickMesh]
    for (const planet of this.planets) {
      if (planet.object.visible) {
        meshes.push(planet.pickMesh)
      }
    }
    return meshes
  }

  /**
   * Resolve a body's data entry by id.
   *
   * @param {string} id - The body id.
   * @returns {CelestialBody | null} The matching body, or null.
   */
  public getBody(id: string): CelestialBody | null {
    const body = this.bodyById.get(id)
    if (body === undefined) {
      return null
    }
    return body.body
  }

  /**
   * Read a body's current world position into the provided target vector.
   *
   * @param {string} id - The body id.
   * @param {THREE.Vector3} target - The vector to write into.
   * @returns {boolean} True if the body was found.
   */
  public getBodyWorldPosition(id: string, target: THREE.Vector3): boolean {
    const position = this.bodyPositions.get(id)
    if (position === undefined) {
      return false
    }
    target.copy(position)
    return true
  }

  /**
   * Highlight a single body and clear the highlight on all others.
   *
   * @param {string | null} id - The body id to highlight, or null to clear.
   * @returns {void}
   */
  public setHighlightedBody(id: string | null): void {
    for (const planet of this.planets) {
      planet.setHighlighted(planet.body.id === id)
    }
  }

  /**
   * Dim or restore the connecting trails (used during focus and warp).
   *
   * @param {number} opacity - Target opacity in the 0..1 range.
   * @returns {void}
   */
  public setTrailOpacity(opacity: number): void {
    this.trails.setOpacity(opacity)
  }

  /**
   * Set the psychedelic intensity of the backdrop (Konami easter egg).
   *
   * @param {number} amount - Target intensity in the 0..1 range.
   * @returns {void}
   */
  public setPsychedelic(amount: number): void {
    this.background.setPsychedelic(amount)
  }

  /**
   * Trigger a solar flare on the central sun.
   *
   * @returns {void}
   */
  public flareSun(): void {
    this.sun.flare()
  }

  /**
   * Send a comet drifting across the backdrop (idle easter egg).
   *
   * @returns {void}
   */
  public launchComet(): void {
    this.background.triggerComet()
  }

  /**
   * Reveal the hidden anomaly body so it can be discovered and entered.
   *
   * @returns {boolean} True if an anomaly existed and was revealed.
   */
  public revealAnomaly(): boolean {
    for (const planet of this.planets) {
      if (planet.body.isHidden) {
        planet.setRevealed(true)
        return true
      }
    }
    return false
  }

  /**
   * Find the sun's data entry, falling back to the first body if none is tagged.
   *
   * @returns {CelestialBody} The sun body.
   */
  private resolveSunBody(): CelestialBody {
    const sunBody = celestialBodies.find((body) => body.tier === 'sun')
    if (sunBody === undefined) {
      return celestialBodies[0]
    }
    return sunBody
  }
}
